import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';

type MarketingLayoutProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

export default function MarketingLayout({ title, subtitle, children }: MarketingLayoutProps) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50 selection:bg-neutral-900 selection:text-white flex flex-col">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-100 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center shadow-lg shadow-neutral-900/20">
              <span className="text-white font-bold text-xl">M</span>
            </div>
            <span className="font-bold text-2xl tracking-tight text-neutral-900 dark:text-neutral-50">Messego</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/services" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Services</Link>
            <Link to="/features" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Features</Link>
            <Link to="/about" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">About</Link>
            <Link to="/contact" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Contact</Link>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/auth/login" className="text-sm font-bold text-neutral-900 dark:text-neutral-50 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors">Sign In</Link>
            <Link to="/auth/register" className="px-6 py-2.5 bg-neutral-900 text-white rounded-xl text-sm font-bold hover:bg-neutral-800 transition-all shadow-lg shadow-neutral-900/20">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      <header className="pt-40 pb-16 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center space-y-4"
        >
          <h1 className="text-5xl md:text-6xl font-black tracking-tight text-neutral-900 dark:text-neutral-50 leading-[1.1]">{title}</h1>
          {subtitle && <p className="text-lg text-neutral-500 dark:text-neutral-400 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
        </motion.div>
      </header>

      <main className="flex-1 px-6 pb-24 max-w-5xl mx-auto w-full">{children}</main>

      <footer className="border-t border-neutral-100 dark:border-neutral-800 px-6 py-16 mt-auto">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-neutral-900 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="font-bold text-xl tracking-tight">Messego</span>
              </div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 leading-relaxed">
                Everything your business needs, in one place.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Product</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/services" className="hover:text-neutral-900 dark:hover:text-neutral-50">Services</Link></li>
                <li><Link to="/features" className="hover:text-neutral-900 dark:hover:text-neutral-50">Features</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Company</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/about" className="hover:text-neutral-900 dark:hover:text-neutral-50">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-neutral-900 dark:hover:text-neutral-50">Careers</Link></li>
                <li><Link to="/press" className="hover:text-neutral-900 dark:hover:text-neutral-50">Press</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Support</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/faq" className="hover:text-neutral-900 dark:hover:text-neutral-50">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-neutral-900 dark:hover:text-neutral-50">Contact</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Legal</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/privacy" className="hover:text-neutral-900 dark:hover:text-neutral-50">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-neutral-900 dark:hover:text-neutral-50">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-neutral-900 dark:hover:text-neutral-50">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-neutral-100 dark:border-neutral-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">© 2026 Messego Inc. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-neutral-400 dark:text-neutral-500">
              <Link to="/privacy" className="hover:text-neutral-900 dark:hover:text-neutral-50">Privacy</Link>
              <Link to="/terms" className="hover:text-neutral-900 dark:hover:text-neutral-50">Terms</Link>
              <Link to="/cookies" className="hover:text-neutral-900 dark:hover:text-neutral-50">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
